/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import { useFloating, autoPlacement, shift, autoUpdate } from '@floating-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteConfirmation from 'components/DeleteConfirmation';
import { IoClose } from 'react-icons/io5';
import { css } from '@emotion/react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EVENT_COLORS } from '../../../utils/constants';
import './style.scss';

dayjs.extend(utc);

const CardContainer = ({
  reference,
  open,
  calendarRef,
  children,
  handleCalendarEventDelete,
  showDeleteButton = false,
  className = '',
  eventDetails = {},
  closeEventPopup,
  hasUnsavedChanges = null,
}) => {
  const [showDeleteConfirm, setDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const ref = useRef(null);

  const eventType = eventDetails?.extendedProps?.type;
  const eventColor =
    EVENT_COLORS[eventType] ||
    eventDetails?.backgroundColor ||
    EVENT_COLORS.default;

  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({
        crossAxis: true,
        padding: { top: 200, left: 200, right: 200 },
      }),
      shift(),
    ],
    elements: { reference },
    open,
  });

  const combineRefs = (...refList) => (node) => {
    refList.forEach((refItem) => {
      if (typeof refItem === 'function') refItem(node);
      else if (refItem) refItem.current = node;
    });
  };

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false);
    closeEventPopup();
  };

  const handleCloseAttempt = () => {
    const isNewEvent = !eventDetails?.id;
    if (
      // New event, with specified changes
      isNewEvent &&
      hasUnsavedChanges &&
      (
        hasUnsavedChanges.eventTitle ||
        hasUnsavedChanges.eventLocation ||
        hasUnsavedChanges.eventNotes ||
        (hasUnsavedChanges.eventStart || hasUnsavedChanges.eventEnd) && hasUnsavedChanges.displayTz
      ) ||
      // Existing event with any changes
      !isNewEvent && hasUnsavedChanges
    ) {
      setShowDiscardConfirm(true);
      return;
    }

    closeEventPopup();
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    // This outside detection may need to be more robust
    // For instance, the task cards spawn an APDropdown component
    // for assignments which ends up outside the card container
    const isOutsideModalClick =
      !e.target.closest('.card-container') &&
      !e.target.closest('.ap-dropdown__menu') &&
      !e.target.closest('.ReactModal__Overlay');
    if (isOutsideModalClick && !hasUnsavedChanges) handleCloseAttempt();
  };

  const handleDeleteConfirm = () => {
    // To avoid losing reference and dealing with
    // floatingStyles positioning, best to just close this now
    closeEventPopup();
    handleCalendarEventDelete();
    setDeleteConfirm(false);
  };

  useEffect(() => {
    // Changing the reference, like when the date of the event changes,
    // doesn't update the useFloating hook and reattach to the new reference.
    // Need to manually update the reference from the returned refs object out of useFloating.
    if (reference && reference !== refs.reference?.current) {
      refs.setReference(reference);
    }
  }, [reference]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownClick =
        event.target.closest('.ap-dropdown__menu') ||
        event.target.classList.contains('ap-dropdown__menu');

      const isModalClick =
        event.target.closest('.delete-confirmation-modal') ||
        event.target.classList.contains('delete-confirmation-modal');

      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !calendarRef.current?.elRef?.current.contains(event.target) &&
        !isDropdownClick &&
        !isModalClick
      ) {
        event.stopPropagation();
        handleCloseAttempt();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref]);

  if (!open) return null;

  return (
    <div
      className="popover-container"
      onMouseDown={handleMouseDown}
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        zIndex: 999,
      }}
    >
      <div
        ref={combineRefs(refs.setFloating, ref)}
        style={floatingStyles}
        className={clsx(
          'card-container',
          className,
        )}
        css={css`
          border-top: 10px solid ${eventColor};
        `}
      >
        {showDeleteButton ? (
          <>
            <i
              className="card-container__icon delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteConfirm(true);
              }}
            >
              <FontAwesomeIcon icon={['fal', 'times-circle']} />
            </i>
            <DeleteConfirmation
              show={showDeleteConfirm}
              handleClose={() => {
                setDeleteConfirm(false);
              }}
              handleConfirm={handleDeleteConfirm}
              deleteText="DELETE EVENT"
              size="xs"
            >
              <p>
                Deleting this calendar event will remove<br />
                it from your <b>account</b> forever.<br />
                Are you sure?
              </p>
            </DeleteConfirmation>
          </>
        ) : (
          <IoClose
            className="card-container__close"
            size={20}
            onClick={handleCloseAttempt}
          />
        )}

        {children}
      </div>

      <DeleteConfirmation
        show={showDiscardConfirm}
        handleClose={() => setShowDiscardConfirm(false)}
        handleConfirm={handleDiscardConfirm}
        deleteText="DISCARD"
        size="xs"
      >
        <p>
          Discard unsaved changes?
        </p>
      </DeleteConfirmation>
    </div>
  );
};

export default CardContainer;
